import React, { useState } from "react";
import { Wrench, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

/**
 * Admin-only button + confirmation flow for the league-overflow repair tool.
 * Calls POST /admin/leagues/repair-overflow and renders a result summary so
 * the admin can verify which leagues got split and how.
 */
export default function RepairOverflowButton() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { repaired, leagues: [...] }

  const run = async () => {
    if (
      !window.confirm(
        "Scan every public league and split any that exceed 20 players into new locked seasons of 20?\n\nThis is safe and idempotent — if no leagues are overcapacity, nothing changes."
      )
    ) {
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/leagues/repair-overflow");
      setResult(data);
      if (!data.repaired) {
        toast.success("All leagues healthy — nothing to repair.");
      } else {
        const total = (data.leagues || []).reduce(
          (s, lg) => s + (lg.spawned || 0),
          0
        );
        toast.success(
          `Repaired ${data.repaired} league${data.repaired === 1 ? "" : "s"} — spawned ${total} new season${total === 1 ? "" : "s"}.`
        );
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] border-l-2 border-l-[#EA580C] p-5 space-y-3"
      data-testid="repair-overflow-panel"
    >
      <div className="flex items-start gap-3">
        <Wrench className="w-5 h-5 text-[#EA580C] flex-shrink-0 mt-1" />
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#EA580C]">
            // maintenance
          </div>
          <div className="font-heading text-base uppercase tracking-wider mt-1">
            Repair Overflow Leagues
          </div>
          <p className="font-body text-xs text-[#71717A] mt-2 leading-relaxed">
            Scans every public league. Any league with more than 20 players is
            trimmed back to 20 (sorted by registration order) and the overflow
            is split into freshly locked seasons of 20 with auto-generated
            fixtures. Any final remainder &lt;20 goes into a new <strong>open</strong>
            league so registrations can keep flowing.
          </p>
        </div>
      </div>

      <button
        type="button"
        onClick={run}
        disabled={busy}
        data-testid="repair-overflow-btn"
        className="inline-flex items-center gap-2 bg-[#EA580C] text-white font-heading uppercase tracking-widest px-5 py-2.5 text-xs hover:bg-[#C2410C] transition-colors disabled:opacity-50 shadow-[0_8px_20px_rgba(234,88,12,0.25)]"
      >
        {busy ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Wrench className="w-3.5 h-3.5" />
        )}
        {busy ? "Scanning..." : "Run Repair Scan"}
      </button>

      {result && (
        <div
          className="bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] p-3 mt-3 space-y-2"
          data-testid="repair-overflow-result"
        >
          {!result.repaired ? (
            <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-[#34C759]">
              <CheckCircle2 className="w-3.5 h-3.5" />
              All leagues are healthy. Nothing was changed.
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-[#EA580C]">
                <AlertTriangle className="w-3.5 h-3.5" />
                Repaired {result.repaired} league{result.repaired === 1 ? "" : "s"}
              </div>
              <ul className="space-y-2 mt-2" data-testid="repair-result-list">
                {(result.leagues || []).map((lg) => (
                  <li
                    key={lg.original_id}
                    className="border-l-2 border-l-[#EA580C] pl-3 py-1"
                  >
                    <div className="font-heading text-sm uppercase">
                      {lg.original_name}
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A]">
                      Kept first {lg.original_kept} players · spawned {lg.spawned} new season{lg.spawned === 1 ? "" : "s"}
                    </div>
                    {lg.new_leagues?.length > 0 && (
                      <ul className="mt-1.5 space-y-0.5 text-xs font-mono">
                        {lg.new_leagues.map((nl) => (
                          <li key={nl.id} className="text-[#52525B] dark:text-[#A1A1AA]">
                            ↳ {nl.name} · {nl.size} players
                            {nl.status === "open" && (
                              <span className="ml-2 text-[#34C759]">[OPEN]</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}
